import * as pdfjsLib from 'pdfjs-dist';
import { zipImageFiles } from '../pdf-to-image/pdfToImageEngine';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

const GET_OBJECT_TIMEOUT_MS = 2500;
const PAGE_COVERAGE_THRESHOLD = 0.65;

async function loadPdf(file) {
    const data = new Uint8Array(await file.arrayBuffer());
    try {
        return await pdfjsLib.getDocument({ data }).promise;
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('password')) {
            throw new Error('password');
        }
        throw err;
    }
}

function multiplyMatrix(current, next) {
    const [a1, b1, c1, d1, e1, f1] = current;
    const [a, b, c, d, e, f] = next;
    return [
        a1 * a + c1 * b,
        b1 * a + d1 * b,
        a1 * c + c1 * d,
        b1 * c + d1 * d,
        a1 * e + c1 * f + e1,
        b1 * e + d1 * f + f1,
    ];
}

function canvasToBlob(canvas, type = 'image/jpeg', quality = 0.92) {
    return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob && blob.size > 0 ? blob : null), type, quality);
    });
}

/**
 * Convert a pdf.js image object into a Blob.
 * Images with transparency are exported as PNG (to avoid the transparent
 * background turning black), opaque images as JPEG.
 * @returns {Promise<{ blob: Blob, ext: string } | null>}
 */
async function imgDataToBlob(imgData) {
    if (!imgData) return null;

    const src = imgData.data;

    // Already-encoded JPEG bytes — pass through untouched.
    if (src instanceof Uint8Array && src[0] === 0xff && src[1] === 0xd8) {
        return { blob: new Blob([src], { type: 'image/jpeg' }), ext: 'jpg' };
    }

    const { width, height, kind } = imgData;
    if (!width || !height || width < 2 || height < 2) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    if (imgData.bitmap instanceof ImageBitmap) {
        ctx.drawImage(imgData.bitmap, 0, 0);
        try { imgData.bitmap.close(); } catch { /* ignore */ }
        return { blob: await canvasToBlob(canvas, 'image/png'), ext: 'png' };
    }

    const pixels = width * height;
    const bitmap = imgData.bitmap instanceof Uint8ClampedArray ? imgData.bitmap : null;
    const data = bitmap || src;
    if (!data) return null;

    const imageData = ctx.createImageData(width, height);
    const dst = imageData.data;
    let hasAlpha = false;

    if (kind === pdfjsLib.ImageKind.RGBA_32BPP || data.length === pixels * 4) {
        dst.set(data.length === pixels * 4 ? data : data.subarray(0, pixels * 4));
        for (let i = 3; i < dst.length; i += 4) {
            if (dst[i] !== 255) { hasAlpha = true; break; }
        }
    } else if (kind === pdfjsLib.ImageKind.RGB_24BPP || data.length === pixels * 3) {
        for (let i = 0, j = 0; j < dst.length; i += 3, j += 4) {
            dst[j] = data[i];
            dst[j + 1] = data[i + 1];
            dst[j + 2] = data[i + 2];
            dst[j + 3] = 255;
        }
    } else if (kind === pdfjsLib.ImageKind.GRAYSCALE_1BPP || data.length === pixels) {
        for (let i = 0, j = 0; i < pixels; i += 1, j += 4) {
            const v = data[i];
            dst[j] = v;
            dst[j + 1] = v;
            dst[j + 2] = v;
            dst[j + 3] = 255;
        }
    } else {
        return null;
    }

    ctx.putImageData(imageData, 0, 0);

    if (hasAlpha) {
        return { blob: await canvasToBlob(canvas, 'image/png'), ext: 'png' };
    }
    return { blob: await canvasToBlob(canvas, 'image/jpeg'), ext: 'jpg' };
}

async function blobFingerprint(blob) {
    const slice = await blob.slice(0, 128).arrayBuffer();
    const bytes = new Uint8Array(slice);
    let hash = blob.size;
    for (let i = 0; i < bytes.length; i++) hash = (hash * 31 + bytes[i]) | 0;
    return hash;
}

async function imageFingerprint(imgData) {
    if (!imgData) return null;
    const source = imgData.data || imgData.bitmap;
    if (!(source instanceof Uint8Array || source instanceof Uint8ClampedArray)) {
        return null;
    }

    const bytes = source.subarray(0, Math.min(128, source.length));
    let hash = ((imgData.width || 0) * 31 + (imgData.height || 0)) | 0;
    hash = (hash * 31 + (imgData.kind || 0)) | 0;
    hash = (hash * 31 + source.length) | 0;
    for (let i = 0; i < bytes.length; i += 1) {
        hash = (hash * 31 + bytes[i]) | 0;
    }
    return hash;
}

function isLikelyPageScan(imgData, page) {
    const width = imgData?.width || 0;
    const height = imgData?.height || 0;
    if (!width || !height) return false;

    const viewport = page.getViewport({ scale: 1 });
    const pageWidth = viewport.width || 0;
    const pageHeight = viewport.height || 0;
    if (!pageWidth || !pageHeight) return false;

    const pageAspect = pageWidth / pageHeight;
    const imageAspect = width / height;
    const aspectDelta = Math.abs(pageAspect - imageAspect);

    return (
        width * height >= 1_500_000
        && width >= pageWidth * 3
        && height >= pageHeight * 3
        && aspectDelta <= 0.03
    );
}

function imageFileName(pdfName, pageNum, index, ext = 'jpg') {
    const base = pdfName.replace(/\.pdf$/i, '');
    return `${base}-p${pageNum}-img${String(index).padStart(3, '0')}.${ext}`;
}

function getPdfObject(pool, id, timeoutMs = GET_OBJECT_TIMEOUT_MS) {
    return new Promise((resolve) => {
        let settled = false;

        const finish = (value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(value);
        };

        const timer = setTimeout(() => finish(null), timeoutMs);

        try {
            pool.get(id, (obj) => finish(obj || null));
        } catch {
            finish(null);
        }
    });
}

function collectImageCandidates(operatorList, page) {
    const viewport = page.getViewport({ scale: 1 });
    const pageWidth = viewport.width || 0;
    const pageHeight = viewport.height || 0;
    const stack = [[1, 0, 0, 1, 0, 0]];
    let ctm = stack[stack.length - 1];
    const seenIds = new Set();
    const candidates = [];

    for (let i = 0; i < operatorList.fnArray.length; i += 1) {
        const fn = operatorList.fnArray[i];
        const args = operatorList.argsArray[i];

        if (fn === pdfjsLib.OPS.save) {
            stack.push([...ctm]);
            ctm = stack[stack.length - 1];
            continue;
        }

        if (fn === pdfjsLib.OPS.restore) {
            if (stack.length > 1) stack.pop();
            ctm = stack[stack.length - 1];
            continue;
        }

        if (fn === pdfjsLib.OPS.transform) {
            ctm = multiplyMatrix(ctm, args);
            stack[stack.length - 1] = ctm;
            continue;
        }

        if (
            fn !== pdfjsLib.OPS.paintImageXObject
            && fn !== pdfjsLib.OPS.paintInlineImageXObject
        ) {
            continue;
        }

        const objectId = typeof args?.[0] === 'string' ? args[0] : null;
        if (!objectId || seenIds.has(objectId)) continue;
        seenIds.add(objectId);

        const [a, b, c, d] = ctm;
        const dispW = Math.hypot(a, b);
        const dispH = Math.hypot(c, d);
        const coversPage = pageWidth > 0 && pageHeight > 0
            && dispW >= pageWidth * PAGE_COVERAGE_THRESHOLD
            && dispH >= pageHeight * PAGE_COVERAGE_THRESHOLD;

        candidates.push({ objectId, coversPage });
    }

    return candidates;
}

async function collectImagesFromPage(page, pageNum, pdfName, globalSeen, failedObjectIds, startIndex) {
    const operatorList = await page.getOperatorList({ intent: 'any' });
    let index = startIndex;
    const candidates = collectImageCandidates(operatorList, page);
    const items = [];

    for (const candidate of candidates) {
        const { objectId, coversPage } = candidate;
        if (coversPage || failedObjectIds.has(objectId)) continue;

        const imgData = await getPdfObject(page.objs, objectId)
            || await getPdfObject(page.commonObjs, objectId);

        if (!imgData) {
            failedObjectIds.add(objectId);
            continue;
        }

        if (isLikelyPageScan(imgData, page)) continue;

        const rawFingerprint = await imageFingerprint(imgData);
        if (rawFingerprint !== null && globalSeen.has(rawFingerprint)) continue;

        const converted = await imgDataToBlob(imgData);
        if (!converted?.blob) continue;

        if (rawFingerprint !== null) {
            globalSeen.add(rawFingerprint);
        } else {
            const fp = await blobFingerprint(converted.blob);
            if (globalSeen.has(fp)) continue;
            globalSeen.add(fp);
        }

        index += 1;
        items.push({
            name: imageFileName(pdfName, pageNum, index, converted.ext),
            blob: converted.blob,
        });
    }

    return { items, nextIndex: index };
}

function buildResult(fileName, images) {
    const totalBytes = images.reduce((sum, item) => sum + item.blob.size, 0);
    const base = fileName.replace(/\.pdf$/i, '');
    return {
        name: images.length === 1 ? images[0].name : `${base}-images.zip`,
        count: images.length,
        totalBytes,
        images,
    };
}

function yieldToMainThread() {
    return new Promise((resolve) => {
        setTimeout(resolve, 0);
    });
}

/**
 * Extract embedded images from a PDF.
 * @param {File} file
 * @param {{
 *   onProgress?: (page: number, total: number) => void,
 *   onPartial?: (result: { name: string, count: number, totalBytes: number, images: { name: string, blob: Blob }[] }) => void,
 * }} [callbacks]
 * @returns {Promise<{ name: string, count: number, totalBytes: number, images: { name: string, blob: Blob }[] }>}
 */
export async function extractPdfImages(file, callbacks = {}) {
    const onProgress = typeof callbacks === 'function' ? callbacks : callbacks.onProgress;
    const onPartial = typeof callbacks === 'function' ? undefined : callbacks.onPartial;

    const pdf = await loadPdf(file);
    const total = pdf.numPages;
    const globalSeen = new Set();
    const failedObjectIds = new Set();
    const extracted = [];
    let imgIndex = 0;

    for (let pageNum = 1; pageNum <= total; pageNum++) {
        onProgress?.(pageNum, total);
        const page = await pdf.getPage(pageNum);
        const { items, nextIndex } = await collectImagesFromPage(
            page,
            pageNum,
            file.name,
            globalSeen,
            failedObjectIds,
            imgIndex,
        );
        extracted.push(...items);
        imgIndex = nextIndex;
        page.cleanup?.();

        if (items.length) {
            onPartial?.(buildResult(file.name, extracted));
        }

        await yieldToMainThread();
    }

    if (!extracted.length) throw new Error('noImages');
    return buildResult(file.name, extracted);
}

export async function buildExtractedImagesDownload(result) {
    if (!result) return null;
    if (result.count === 1) {
        return { name: result.name, blob: result.images[0]?.blob || null };
    }
    return {
        name: result.name,
        blob: await zipImageFiles(result.images),
    };
}
